import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, getDoc, deleteDoc, updateDoc, setDoc, orderBy, query, serverTimestamp } from "firebase/firestore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { db } from "../../firebase/config";
import { formatCurrency } from "../../utils/pricing";
import { toast } from "react-toastify";

export default function ManageOffers() {
  const [offers, setOffers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [updating, setUpdating] = useState(null);
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [viewingOffer, setViewingOffer] = useState(null);

  // Offers Form states
  const [formData, setFormData] = useState({
    code: "",
    description: "",
    discountType: "percentage",
    discountValue: "",
    minSpend: "",
    showToAll: true,
    selectedUsers: [],
    isActive: true,
    expiryDate: "",
    usageLimit: "",
  });

  // Banner Settings State
  const [bannerSettings, setBannerSettings] = useState({
    offerEnabled: true,
    offerLabel: "Limited Offer",
    offerTitle1: "Free Delivery",
    offerTitle2: "Today Only",
    offerDescription: "Use code FREESHIP at checkout. No minimum order required.",
    offerButtonText: "Claim Offer",
    offerLink: "/products",
    offerDiscountType: "percentage",
    offerDiscountValue: "",
    offerMinSpend: "",
  });
  const [savingBanner, setSavingBanner] = useState(false);

  // Error States
  const [bannerError, setBannerError] = useState("");
  const [formErrorCode, setFormErrorCode] = useState("");
  const [formErrorDiscount, setFormErrorDiscount] = useState("");

  async function fetchData() {
    setLoading(true);
    try {
      const [offersSnap, usersSnap, settingsSnap] = await Promise.all([
        getDocs(query(collection(db, "offers"), orderBy("createdAt", "desc"))).catch(() => ({ docs: [] })),
        getDocs(query(collection(db, "users"), orderBy("createdAt", "desc"))).catch(() => ({ docs: [] })),
        getDoc(doc(db, "settings", "general")).catch(() => ({ exists: () => false, data: () => ({}) }))
      ]);
      
      setOffers(offersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setUsers(usersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      
      if (settingsSnap.exists()) {
        setBannerSettings((prev) => ({ ...prev, ...settingsSnap.data() }));
      }
    } catch (err) {
      console.error("Failed to fetch data:", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchData(); }, []);

  // --- Banner Handlers ---
  const handleBannerChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBannerSettings((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSaveBanner = async (e) => {
    e.preventDefault();
    setBannerError("");

    const bannerDiscountVal = Number(bannerSettings.offerDiscountValue);
    if (bannerSettings.offerDiscountType === "percentage" && bannerDiscountVal > 80) {
      setBannerError("Percentage discount cannot be more than 80%.");
      return;
    }
    if (bannerSettings.offerDiscountType === "fixed" && bannerDiscountVal > 100000) {
      setBannerError("Fixed discount cannot be more than 100,000.");
      return;
    }

    setSavingBanner(true);
    try {
      await setDoc(doc(db, "settings", "general"), {
        ...bannerSettings,
        offerDiscountValue: Number(bannerSettings.offerDiscountValue),
        offerMinSpend: Number(bannerSettings.offerMinSpend) || 0
      }, { merge: true });
      toast.success("Promotional Banner saved!");
    } catch (err) {
      console.error("Error saving banner:", err);
      toast.error("Failed to save banner.");
    } finally {
      setSavingBanner(false);
    }
  };

  // --- Offers Handlers ---
  function handleOpenModal(offer = null) {
    if (offer) {
      setEditingOffer(offer);
      setFormData({
        code: offer.code || "",
        description: offer.description || "",
        discountType: offer.discountType || "percentage",
        discountValue: offer.discountValue || "",
        minSpend: offer.minSpend || "",
        showToAll: offer.showToAll !== false,
        selectedUsers: offer.selectedUsers || [],
        isActive: offer.isActive !== false,
        expiryDate: offer.expiryDate || "",
        usageLimit: offer.usageLimit || "",
      });
    } else {
      setEditingOffer(null);
      setFormData({
        code: "",
        description: "",
        discountType: "percentage",
        discountValue: "",
        minSpend: "",
        showToAll: true,
        selectedUsers: [],
        isActive: true,
        expiryDate: "",
        usageLimit: "",
      });
    }
    setIsModalOpen(true);
    setFormErrorCode("");
    setFormErrorDiscount("");
  }

  function handleCloseModal() {
    setIsModalOpen(false);
    setEditingOffer(null);
  }

  function handleUserSelect(email) {
    setFormData(prev => {
      const isSelected = prev.selectedUsers.includes(email);
      if (isSelected) {
        return { ...prev, selectedUsers: prev.selectedUsers.filter(e => e !== email) };
      } else {
        return { ...prev, selectedUsers: [...prev.selectedUsers, email] };
      }
    });
  }

  async function handleSave() {
    setFormErrorCode("");
    setFormErrorDiscount("");
    let hasError = false;

    if (!formData.code) {
      setFormErrorCode("Please enter a coupon code.");
      hasError = true;
    }
    if (formData.discountValue === "") {
      setFormErrorDiscount("Please enter a discount value.");
      hasError = true;
    }

    const codeUpper = formData.code.toUpperCase();
    const discountNum = Number(formData.discountValue);

    // 1. Unique code validation
    if (formData.code) {
      const isDuplicate = offers.some(o => o.code.toUpperCase() === codeUpper && o.id !== editingOffer?.id);
      if (isDuplicate) {
        setFormErrorCode("A coupon with this code already exists.");
        hasError = true;
      }
    }

    // 2. Percentage validation (max 80%)
    if (formData.discountType === "percentage" && discountNum > 80) {
      setFormErrorDiscount("Percentage discount cannot be more than 80%.");
      hasError = true;
    }

    // 3. Fixed value validation (max 100000)
    if (formData.discountType === "fixed" && discountNum > 100000) {
      setFormErrorDiscount("Fixed discount cannot be more than 100,000.");
      hasError = true;
    }

    if (hasError) return;

    try {
      const offerData = {
        code: codeUpper,
        description: formData.description,
        discountType: formData.discountType,
        discountValue: Number(formData.discountValue),
        minSpend: Number(formData.minSpend) || 0,
        showToAll: formData.showToAll,
        selectedUsers: formData.showToAll ? [] : formData.selectedUsers,
        isActive: formData.isActive,
        expiryDate: formData.expiryDate || null,
        usageLimit: formData.usageLimit ? Number(formData.usageLimit) : null,
        updatedAt: serverTimestamp(),
      };

      if (editingOffer) {
        await updateDoc(doc(db, "offers", editingOffer.id), offerData);
        setOffers(prev => prev.map(o => o.id === editingOffer.id ? { ...o, ...offerData, updatedAt: new Date() } : o));
      } else {
        offerData.usedBy = []; // Initialize empty array for usage tracking
        offerData.usesCount = 0; // Track total uses
        offerData.createdAt = serverTimestamp();
        const newDocRef = doc(collection(db, "offers"));
        await setDoc(newDocRef, offerData);
        setOffers([{ id: newDocRef.id, ...offerData, createdAt: new Date() }, ...offers]);
      }
      handleCloseModal();
      toast.success("Coupon saved successfully!");
    } catch (err) {
      console.error("Save offer failed:", err);
      toast.error("Failed to save offer.");
    }
  }

  async function handleDelete(id) {
    try {
      await deleteDoc(doc(db, "offers", id));
      setOffers((prev) => prev.filter((o) => o.id !== id));
      toast.success("Coupon deleted!");
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error("Failed to delete coupon.");
    } finally {
      setConfirmDelete(null);
    }
  }

  async function handleStatusToggle(id, currentStatus) {
    try {
      setUpdating(id);
      await updateDoc(doc(db, "offers", id), { isActive: !currentStatus });
      setOffers((prev) => prev.map((o) => o.id === id ? { ...o, isActive: !currentStatus } : o));
    } catch (err) {
      console.error("Status update failed:", err);
    } finally {
      setUpdating(null);
    }
  }

  const filtered = offers.filter(
    (o) =>
      !search ||
      (o.code || "").toLowerCase().includes(search.toLowerCase()) ||
      (o.description || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-extrabold text-gray-900">Offers & Coupons</h1>
        <p className="text-sm text-gray-500">Manage promotional banner and generated coupons.</p>
      </div>

      {/* --- Promotional Banner Section --- */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden p-6 border border-gray-100">
        <h2 className="text-lg font-bold text-gray-800 border-b pb-4 mb-4">Promotional Offer Banner</h2>
        {loading ? (
          <div className="text-sm text-gray-400 animate-pulse">Loading banner settings...</div>
        ) : (
          <form onSubmit={handleSaveBanner} className="space-y-5">
            {/* Toggle Enable/Disable */}
            <label className="flex items-center gap-3 cursor-pointer">
              <div className="relative">
                <input
                  type="checkbox"
                  name="offerEnabled"
                  checked={bannerSettings.offerEnabled}
                  onChange={handleBannerChange}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
              </div>
              <span className="font-medium text-gray-800">Enable Offer Banner</span>
            </label>

            {bannerSettings.offerEnabled && (
              <div className="space-y-4 pt-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Badge Text</label>
                  <input
                    type="text"
                    name="offerLabel"
                    value={bannerSettings.offerLabel}
                    onChange={handleBannerChange}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g. Limited Offer"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title Part 1 (White Text)</label>
                    <input
                      type="text"
                      name="offerTitle1"
                      value={bannerSettings.offerTitle1}
                      onChange={handleBannerChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500"
                      placeholder="e.g. Free Delivery"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title Part 2 (Gold Text)</label>
                    <input
                      type="text"
                      name="offerTitle2"
                      value={bannerSettings.offerTitle2}
                      onChange={handleBannerChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500"
                      placeholder="e.g. Today Only"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description (Subtitle)</label>
                  <input
                    type="text"
                    name="offerDescription"
                    value={bannerSettings.offerDescription}
                    onChange={handleBannerChange}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g. Use code FREESHIP at checkout..."
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Discount Type</label>
                    <select
                      name="offerDiscountType"
                      value={bannerSettings.offerDiscountType || "percentage"}
                      onChange={handleBannerChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Discount Value *</label>
                    <input
                      type="number"
                      name="offerDiscountValue"
                      value={bannerSettings.offerDiscountValue}
                      onChange={(e) => { handleBannerChange(e); setBannerError(''); }}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500"
                      placeholder={bannerSettings.offerDiscountType === 'percentage' ? "e.g. 10" : "e.g. 500"}
                      required
                    />
                    {bannerError && <p className="text-red-500 text-xs mt-1 font-semibold">{bannerError}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Spend (Optional)</label>
                    <input
                      type="number"
                      name="offerMinSpend"
                      value={bannerSettings.offerMinSpend}
                      onChange={handleBannerChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500"
                      placeholder="e.g. 1000"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                    <input
                      type="text"
                      name="offerButtonText"
                      value={bannerSettings.offerButtonText}
                      onChange={handleBannerChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500"
                      placeholder="e.g. Claim Offer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Button Link</label>
                    <input
                      type="text"
                      name="offerLink"
                      value={bannerSettings.offerLink}
                      onChange={handleBannerChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500"
                      placeholder="e.g. /products"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="pt-2 flex justify-end">
              <button
                type="submit"
                disabled={savingBanner}
                className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors disabled:opacity-50"
              >
                {savingBanner ? "Saving..." : "Save Banner"}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* --- Individual Coupons Section --- */}
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Discount Coupons</h2>
            <p className="text-sm text-gray-500">Create multiple checkout coupons targeting specific audiences.</p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="bg-gray-900 text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-gray-800 transition"
          >
            + Create Coupon
          </button>
        </div>

        <input
          type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by code or description…"
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 w-72"
        />

        <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
          {loading ? (
            <div className="p-8 text-center text-gray-400 animate-pulse">Loading offers…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-gray-400">No offers found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-5 py-3">Code</th>
                    <th className="text-left px-5 py-3">Discount</th>
                    <th className="text-left px-5 py-3">Min Spend</th>
                    <th className="text-left px-5 py-3">Expiry</th>
                    <th className="text-left px-5 py-3">Limits</th>
                    <th className="text-left px-5 py-3">Audience</th>
                    <th className="text-left px-5 py-3">Status</th>
                    <th className="text-center px-5 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((offer) => (
                     <tr key={offer.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3">
                        <div>
                          <p className="font-bold text-gray-800">{offer.code}</p>
                          <p className="text-xs text-gray-500 max-w-[200px] truncate" title={offer.description}>
                            {offer.description || "No description"}
                          </p>
                        </div>
                      </td>
                      <td className="px-5 py-3 font-semibold text-gray-700">
                        {offer.discountType === "percentage" ? `${offer.discountValue}%` : formatCurrency(offer.discountValue)}
                      </td>
                      <td className="px-5 py-3 text-gray-600">
                        {offer.minSpend > 0 ? formatCurrency(offer.minSpend) : "None"}
                      </td>
                      <td className="px-5 py-3 text-gray-600 text-xs">
                        {offer.expiryDate ? new Date(offer.expiryDate).toLocaleDateString() : "No Expiry"}
                      </td>
                      <td className="px-5 py-3 text-gray-600 text-xs">
                        {offer.usageLimit ? `${offer.usesCount || 0}/${offer.usageLimit}` : "Unlimited"}
                      </td>
                      <td className="px-5 py-3">
                        {offer.showToAll ? (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-md font-medium">All Users</span>
                        ) : (
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-md font-medium" title={offer.selectedUsers?.join(", ")}>
                            {offer.selectedUsers?.length || 0} User(s)
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md ${
                            offer.isActive ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"
                          }`}>
                            {offer.isActive ? "Active" : "Disabled"}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleStatusToggle(offer.id, offer.isActive)}
                            disabled={updating === offer.id}
                            className={`text-xs px-3 py-1 rounded-lg font-medium transition-colors disabled:opacity-50 ${
                              offer.isActive
                                ? "bg-orange-50 text-orange-600 hover:bg-orange-100"
                                : "bg-green-50 text-green-700 hover:bg-green-100"
                            }`}
                          >
                            {offer.isActive ? "Disable" : "Enable"}
                          </button>
                          <button
                            onClick={() => setViewingOffer(offer)}
                            title="View Details & Usage"
                            className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 font-medium transition-colors"
                          >
                            <VisibilityOutlinedIcon fontSize="small" />
                          </button>
                          <button onClick={() => handleOpenModal(offer)} title="Edit"
                            className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 font-medium transition-colors">
                            <EditOutlinedIcon fontSize="small" />
                          </button>
                          <button onClick={() => setConfirmDelete(offer)} title="Delete" disabled={updating === offer.id}
                            className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 font-medium transition-colors disabled:opacity-50">
                            <DeleteOutlineIcon fontSize="small" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {editingOffer ? "Edit Coupon" : "Create New Coupon"}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Coupon Code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => { setFormData({ ...formData, code: e.target.value }); setFormErrorCode(''); }}
                  placeholder="e.g. SUMMER10"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm uppercase focus:ring-2 focus:ring-purple-500"
                />
                {formErrorCode && <p className="text-red-500 text-xs mt-1 font-semibold">{formErrorCode}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Discount Type</label>
                <select
                  value={formData.discountType}
                  onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Discount Value *</label>
                <input
                  type="number"
                  value={formData.discountValue}
                  onChange={(e) => { setFormData({ ...formData, discountValue: e.target.value }); setFormErrorDiscount(''); }}
                  placeholder={formData.discountType === 'percentage' ? "e.g. 10" : "e.g. 500"}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                />
                {formErrorDiscount && <p className="text-red-500 text-xs mt-1 font-semibold">{formErrorDiscount}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Minimum Spend (Optional)</label>
                <input
                  type="number"
                  value={formData.minSpend}
                  onChange={(e) => setFormData({ ...formData, minSpend: e.target.value })}
                  placeholder="e.g. 1000"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Expiry Date</label>
                <input
                  type="date"
                  value={formData.expiryDate}
                  onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Usage Limit (Total Uses)</label>
                <input
                  type="number"
                  value={formData.usageLimit}
                  onChange={(e) => setFormData({ ...formData, usageLimit: e.target.value })}
                  placeholder="e.g. 100"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Short description of the offer..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                rows="2"
              ></textarea>
            </div>

            <div className="mb-4 pb-4 border-b border-gray-100">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500 cursor-pointer"
                />
                <span className="text-sm font-semibold text-gray-800">Is Active</span>
              </label>
            </div>

            <div className="mb-4">
              <label className="flex items-center gap-2 mb-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.showToAll}
                  onChange={(e) => setFormData({ ...formData, showToAll: e.target.checked })}
                  className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500 cursor-pointer"
                />
                <span className="text-sm font-semibold text-gray-800">Available to All Users</span>
              </label>
              
              {!formData.showToAll && (
                <div className="mt-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Select Target Users:</label>
                  <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto p-2 space-y-1">
                    {users.map(u => (
                      <label key={u.id} className="flex items-center gap-2 p-1.5 hover:bg-gray-50 rounded cursor-pointer">
                        <input 
                          type="checkbox"
                          checked={formData.selectedUsers.includes(u.email)}
                          onChange={() => handleUserSelect(u.email)}
                          className="w-4 h-4 text-purple-600 rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-700">{u.displayName || "No Name"} ({u.email})</span>
                      </label>
                    ))}
                    {users.length === 0 && <p className="text-sm text-gray-400 p-2">No users found.</p>}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
              <button onClick={handleCloseModal} className="px-5 py-2.5 border border-gray-300 rounded-xl text-sm font-medium hover:bg-gray-50">Cancel</button>
              <button onClick={handleSave} className="px-5 py-2.5 bg-purple-600 text-white rounded-xl text-sm font-semibold hover:bg-purple-700">Save Coupon</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <p className="text-4xl mb-3">🗑️</p>
            <h3 className="font-bold text-gray-900 mb-2">Delete Coupon?</h3>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to permanently delete coupon <strong>{confirmDelete.code}</strong>?
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm font-medium hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleDelete(confirmDelete.id)} className="flex-1 py-2.5 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* View Details & Usage Modal */}
      {viewingOffer && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-0 border border-gray-100">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <span className="text-2xl">🎫</span> {viewingOffer.code}
                </h3>
                <p className="text-sm text-gray-500 uppercase tracking-widest font-bold mt-1">Coupon Details</p>
              </div>
              <button onClick={() => setViewingOffer(null)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
            </div>

            <div className="p-6">
              {/* Info Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                  <p className="text-[10px] text-gray-400 uppercase font-bold mb-1">Discount</p>
                  <p className="text-lg font-extrabold text-blue-600">
                    {viewingOffer.discountType === "percentage" ? `${viewingOffer.discountValue}%` : formatCurrency(viewingOffer.discountValue)}
                  </p>
                </div>
                <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                  <p className="text-[10px] text-gray-400 uppercase font-bold mb-1">Min Spend</p>
                  <p className="text-lg font-extrabold text-gray-700">
                    {viewingOffer.minSpend > 0 ? formatCurrency(viewingOffer.minSpend) : "None"}
                  </p>
                </div>
                <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                  <p className="text-[10px] text-gray-400 uppercase font-bold mb-1">Status</p>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md ${viewingOffer.isActive ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"}`}>
                    {viewingOffer.isActive ? "Active" : "Disabled"}
                  </span>
                </div>
                <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                  <p className="text-[10px] text-gray-400 uppercase font-bold mb-1">Usage</p>
                  <p className="text-lg font-extrabold text-purple-600">
                    {viewingOffer.usesCount || 0} / {viewingOffer.usageLimit || "∞"}
                  </p>
                </div>
              </div>

              <div className="mb-8 p-4 bg-yellow-50 rounded-xl border border-yellow-100">
                <p className="text-[10px] text-yellow-700 uppercase font-bold mb-1">Description</p>
                <p className="text-sm text-yellow-900 leading-relaxed">{viewingOffer.description || "No description provided."}</p>
              </div>

              {/* Usage List */}
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b pb-2">
                  <h4 className="text-md font-bold text-gray-800 flex items-center gap-2">
                    <span className="p-1.5 bg-purple-100 rounded-lg text-sm">👥</span> Redemptions History
                  </h4>
                  <span className="text-xs text-gray-500 font-medium">{(viewingOffer.usedBy || []).length} users total</span>
                </div>

                <div className="max-h-60 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                  {(viewingOffer.usedBy || []).length === 0 ? (
                    <div className="text-center py-10 text-gray-400 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-100">
                      <p className="text-2xl mb-2">🤷</p>
                      <p className="text-sm">No one has used this coupon yet.</p>
                    </div>
                  ) : (
                    viewingOffer.usedBy.map((uid) => {
                      const user = users.find((u) => u.id === uid);
                      return (
                        <div key={uid} className="flex items-center gap-3 p-3 bg-white border border-gray-100 rounded-xl hover:shadow-sm transition-shadow">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center text-purple-600 font-bold text-sm">
                            {user?.displayName ? user.displayName.charAt(0).toUpperCase() : "?"}
                          </div>
                          <div>
                            <p className="text-sm font-bold text-gray-800">{user?.displayName || "Unknown User"}</p>
                            <p className="text-xs text-gray-500">{user?.email || "No email linked"}</p>
                          </div>
                          <div className="ml-auto text-[10px] bg-blue-50 text-blue-600 px-2 py-1 rounded-md font-bold uppercase tracking-tighter">
                            Redeemed
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end">
              <button 
                onClick={() => setViewingOffer(null)} 
                className="px-8 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-bold hover:bg-gray-800 transition"
              >
                Close View
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
